// Utils
import { getMaxHeight, getWidth } from '../../../common/elements/utils/elementPropertyUtils';
import { isChangeTypeofRectSize } from '../resizing/resizeUtils';

// Hooks
import useGroupResizeBehaviour, { ResizeProps } from '../resizing/useGroupResizeBehaviour';

// Types
import { ImMNElement } from '../../../common/elements/elementModelTypes';
import { Point } from '../../../common/maths/geometry/pointTypes';
import { ResizeChanges, SetElementSizeAndPosition } from '../resizing/resizeTypes';
import { MutableRefObject } from 'react';
import { ELEMENT_DEFAULT_WIDTH } from '../../../common/elements/elementConstants';
import { DispatchUpdateElement } from '../actions/elementActionTypes';

type Props = ResizeProps & {
    elementId: string;
    element: ImMNElement;
    gridSize: number;
    setElementSizeAndPosition: SetElementSizeAndPosition;
    dispatchUpdateElement: DispatchUpdateElement;
};

const useExpandedCommentThreadResize = (
    props: Props,
    contentContainerRef: MutableRefObject<HTMLDivElement>,
    contentRef: MutableRefObject<HTMLDivElement>,
) => {
    const { elementId, element, gridSize, dispatchUpdateElement } = props;

    const setElementSizeAndPosition = (size: ResizeChanges, position?: Point) => {
        if (!isChangeTypeofRectSize(size)) {
            if (position) {
                props.setElementSizeAndPosition(null, position);
            }
            return;
        }

        const changes: { width: number; maxHeight?: number | string } = {
            width: Math.max(Math.round(size.width), ELEMENT_DEFAULT_WIDTH),
        };

        const contentHeight = contentRef.current ? contentRef.current.clientHeight : 0;
        const containerHeight = contentContainerRef.current ? contentContainerRef.current.clientHeight : Infinity;

        const shouldSaveMaxHeight =
            contentHeight > containerHeight ||
            // Or already has maxHeight saved
            !!getMaxHeight(element);

        if (shouldSaveMaxHeight) {
            // If the container height isn't as big as the resize height, then use the container height instead
            changes.maxHeight =
                contentHeight >= size.height * gridSize ? Math.round(containerHeight / gridSize) : 'none';
        }

        props.setElementSizeAndPosition(changes, position);
    };

    const handleDoubleClick = () => {
        const width = getWidth(element);
        const maxHeight = getMaxHeight(element);

        if ((!width || width === ELEMENT_DEFAULT_WIDTH) && !maxHeight) return;

        return dispatchUpdateElement({
            id: elementId,
            changes: {
                width: null,
                maxHeight: null,
            },
        });
    };

    useGroupResizeBehaviour({ ...props, setElementSizeAndPosition });

    return { handleDoubleClick };
};

export default useExpandedCommentThreadResize;
